import { useCallback, useState, useEffect, useMemo } from "react";
import {
  DataTableControllerType,
  DefaultRecordType,
  DefaultFilterValueType,
  DataTableOnLoadDataType,
} from "../types/index";
import { useQueueProcess } from "@/app/hook/use-queue-process";
import { DataTableControllerStoreType } from "../types/index";

const defaultPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
};

export const useController = <
  RecordType = DefaultRecordType,
  FilterValueType = DefaultFilterValueType,
>(
  keyProcess: DataTableControllerType = "data-table",
) => {
  const { addQueueProcess, currentProcess } =
    useQueueProcess<DataTableControllerStoreType<RecordType, FilterValueType>>(
      keyProcess,
    );

  const [controllerStore, setControllerStore] = useState<
    DataTableControllerStoreType<RecordType, FilterValueType>
  >({
    initial: false,
    onLoadData: undefined,
    search: undefined,
    filterValue: undefined,
    dataSource: [],
    dataSourceTotal: undefined,
    pagination: defaultPagination,
    ["__UPDATE__"]: Symbol("STORE_VERSION_KEY"),
  });

  const updateControllerStore = (
    setStore: (
      store: DataTableControllerStoreType<RecordType, FilterValueType>,
    ) => Partial<DataTableControllerStoreType<RecordType, FilterValueType>>,
  ) => {
    setControllerStore((controllerStore) => {
      const newStore = {
        ...controllerStore,
        ...setStore(controllerStore),
        ["__UPDATE__"]: Symbol("STORE_VERSION_KEY"),
      };

      return newStore;
    });
  };

  const innerLoadData = () => {
    if (!controllerStore?.onLoadData) {
      return;
    }

    return addQueueProcess({
      key: keyProcess,
      data: { ...controllerStore },
      promise: new Promise((resolveQueueProcess, rejectQueueProcess) => {
        controllerStore.onLoadData!(controllerStore!)
          .then((data) => {
            updateControllerStore(() => ({
              initial: true,
              dataSourceTotal: data?.pagination?.total || 0,
              ...data,
            }));
            resolveQueueProcess(data);
          })
          .catch((error) => {
            rejectQueueProcess(error);
          });
      }),
    });
  };

  const setLoadData = (
    onLoadData: DataTableOnLoadDataType<RecordType, FilterValueType>,
  ) => {
    updateControllerStore(() => ({ onLoadData }));
  };

  const setPagination = useCallback((currentPage: number, pageSize: number) => {
    updateControllerStore((store) => ({
      pagination: {
        ...store.pagination,
        current: currentPage,
        pageSize,
      },
    }));
  }, []);

  const setFilter = useCallback((filterValue: FilterValueType) => {
    updateControllerStore(() => ({
      filterValue: filterValue,
    }));
  }, []);

  const setSearch = useCallback((search: string) => {
    updateControllerStore(() => ({
      search: search?.trim(),
    }));
  }, []);

  const setDataSource = useCallback(
    (dataSource: RecordType[], total?: number | undefined) => {
      updateControllerStore(() => ({
        initial: true,
        dataSource,
        dataSourceTotal: total || dataSource?.length || 0,
      }));
    },
    [],
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (
      controllerStore?.initial &&
      (controllerStore?.search !== undefined ||
        controllerStore?.filterValue !== undefined)
    ) {
      timeout = setTimeout(() => {
        innerLoadData();
      }, 900);
    }

    return () => clearTimeout(timeout);
  }, [controllerStore?.search, controllerStore?.filterValue]);

  useEffect(() => {
    if (controllerStore?.initial) {
      innerLoadData();
    }
  }, [
    controllerStore?.pagination?.current,
    controllerStore?.pagination?.pageSize,
  ]);

  return useMemo(
    () => ({
      loadData: innerLoadData,
      setSearch,
      setFilter,
      setPagination,
      setDataSource,
      setLoadData,
      ...controllerStore,
      currentProcess,
    }),
    [controllerStore["__UPDATE__"], currentProcess?.status],
  );
};
